import { makeAutoObservable, runInAction } from 'mobx';
import orderService from '../services/orderService';

class PartnerOrderStore {
  orders = [];
  currentOrder = null;
  loading = false;
  error = null;

  // Filters
  statusFilter = null; // PENDING, CONFIRMED, SHIPPED, DELIVERED, CANCELLED
  searchQuery = '';

  constructor() {
    makeAutoObservable(this);
  }

  // Computed values
  get filteredOrders() {
    let filtered = this.orders;

    // Filter by status
    if (this.statusFilter) {
      filtered = filtered.filter(order => order.status === this.statusFilter);
    }

    // Filter by search query (order number, customer name)
    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      filtered = filtered.filter(order =>
        order.id.toString().includes(query) ||
        order.user_email?.toLowerCase().includes(query) ||
        `${order.user_first_name} ${order.user_last_name}`.toLowerCase().includes(query)
      );
    }

    return filtered;
  }

  // Actions
  setStatusFilter(status) {
    this.statusFilter = status;
  }

  setSearchQuery(query) {
    this.searchQuery = query;
  }

  async fetchOrders(params = {}) {
    this.loading = true;
    this.error = null;

    try {
      const result = await orderService.getPartnerOrders(params);

      runInAction(() => {
        if (result.success) {
          this.orders = result.data;
        } else {
          this.error = result.error || 'Failed to fetch orders';
        }
        this.loading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error.message || 'Failed to fetch orders';
        this.loading = false;
      });
      return { success: false, error: this.error };
    }
  }

  async fetchOrderById(orderId) {
    this.loading = true;
    this.error = null;

    try {
      const result = await orderService.getPartnerOrderById(orderId);

      runInAction(() => {
        if (result.success) {
          this.currentOrder = result.data;
        } else {
          this.error = result.error || 'Failed to fetch order';
        }
        this.loading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error.message || 'Failed to fetch order';
        this.loading = false;
      });
      return { success: false, error: this.error };
    }
  }

  async updateOrderStatus(orderId, status, notes = '') {
    this.loading = true;
    this.error = null;

    try {
      const result = await orderService.updateOrderStatus(orderId, { status, notes });

      runInAction(() => {
        if (result.success) {
          // The API returns the updated order directly in result.data
          const updatedOrder = result.data.order || result.data;

          // Update current order if it's the same one
          if (this.currentOrder && this.currentOrder.id === parseInt(orderId)) {
            this.currentOrder = updatedOrder;
          }

          // Update in orders list
          const index = this.orders.findIndex(o => o.id === parseInt(orderId));
          if (index !== -1) {
            this.orders[index] = updatedOrder;
          }
        } else {
          this.error = result.error || 'Failed to update order status';
        }
        this.loading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error.message || 'Failed to update order status';
        this.loading = false;
      });
      return { success: false, error: this.error };
    }
  }

  // Reset store
  reset() {
    this.orders = [];
    this.currentOrder = null;
    this.loading = false;
    this.error = null;
    this.statusFilter = null;
    this.searchQuery = '';
  }
}

export default PartnerOrderStore;
